"use client";
import dynamic from "next/dynamic";
import React, { useMemo } from "react";
import { cleanHeaderTags, createLookupObject, decodeHtml } from "@/lib/helper";
import HomeBannerSlider from "@/components/home/HomeBannerSlider";
import { OptimizedImage } from "@/components/common/OptimizedImage";

const PerfectForSection = dynamic(
  () =>
    import("@/components/PerfectForSection").then(
      (mod) => mod.PerfectForSection,
    ),
  {
    ssr: false,
    loading: () => (
      <div className="min-h-[500px] animate-pulse bg-gray-50/50" />
    ),
  },
);
const DiningSection = dynamic(
  () =>
    import("@/components/remaining-sections").then((mod) => mod.DiningSection),
  {
    ssr: false,
    loading: () => (
      <div className="min-h-[600px] animate-pulse bg-gray-50/50" />
    ),
  },
);
const ExperiencesSection = dynamic(
  () =>
    import("@/components/remaining-sections").then(
      (mod) => mod.ExperiencesSection,
    ),
  {
    ssr: false,
    loading: () => (
      <div className="min-h-[800px] animate-pulse bg-gray-50/50" />
    ),
  },
);
const FAQSection = dynamic(
  () => import("@/components/remaining-sections").then((mod) => mod.FAQSection),
  {
    ssr: false,
    loading: () => (
      <div className="min-h-[1000px] animate-pulse bg-gray-50/50" />
    ),
  },
);
const GallerySection = dynamic(
  () =>
    import("@/components/remaining-sections").then((mod) => mod.GallerySection),
  {
    ssr: false,
    loading: () => (
      <div className="min-h-[480px] animate-pulse bg-gray-50/50" />
    ),
  },
);
const GirSection = dynamic(
  () => import("@/components/remaining-sections").then((mod) => mod.GirSection),
  {
    ssr: false,
    loading: () => (
      <div className="min-h-[600px] animate-pulse bg-gray-50/50" />
    ),
  },
);
const TestimonialsSection = dynamic(
  () =>
    import("@/components/remaining-sections").then(
      (mod) => mod.TestimonialsSection,
    ),
  {
    ssr: false,
    loading: () => (
      <div className="min-h-[480px] animate-pulse bg-gray-50/50" />
    ),
  },
);

const Home = ({
  initialData,
  cmsData,
  bannerData,
}: {
  initialData?: any;
  cmsData?: any;
  bannerData?: any;
}) => {
  const homeDetails = initialData;
  const CMSList = cmsData;
  const BannerList = bannerData;

  const lookUpCMSData = useMemo(() => {
    const list =
      CMSList && CMSList.length > 0 ? CMSList : homeDetails?.CmsList || [];
    return createLookupObject(list, "Name");
  }, [CMSList, homeDetails?.CmsList]);

  const banner =
    BannerList?.filter((banner: any) => banner?.BannerType === "home") || [];

  return (
    <div className="min-h-screen pt-24 md:pt-0">
      {" "}
      {/* Added padding for fixed header on mobile, stable layout */}
      {/* Premium Hero Section - Forced height to prevent CLS */}
      <section className="h-[100dvh] w-full bg-gray-900 overflow-hidden relative">
        {banner && banner?.length > 0 ? (
          <HomeBannerSlider data={banner} />
        ) : (
          <div className="h-full w-full animate-pulse bg-gray-800" />
        )}
      </section>
      {/* Premium Stats Bar - Stabilized height */}
      <div className="min-h-[100px] w-full bg-white border-b border-[#D8C4A8]/20 flex items-center overflow-hidden">
        {lookUpCMSData?.["Home - SECTION 2: Stats"] && (
          <div
            className="w-full h-full"
            dangerouslySetInnerHTML={{
              __html: cleanHeaderTags(
                decodeHtml(
                  lookUpCMSData?.["Home - SECTION 2: Stats"]?.Template,
                ),
              ),
            }}
          />
        )}
      </div>
      {/* Premium Amenities Showcase */}
      {homeDetails &&
      homeDetails?.WhyChooseUs &&
      homeDetails?.WhyChooseUs?.length > 0 ? (
        <section className="py-24 bg-gradient-to-b from-white to-[#F3EBDD]/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {lookUpCMSData?.["Home - SECTION 3: Why Choose Header"] && (
              <div
                dangerouslySetInnerHTML={{
                  __html: cleanHeaderTags(
                    decodeHtml(
                      lookUpCMSData?.["Home - SECTION 3: Why Choose Header"]
                        ?.Template,
                    ),
                  ),
                }}
              />
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {homeDetails?.WhyChooseUs?.map((item: any) => (
                <div
                  key={item.ID}
                  className="group p-8 rounded-3xl bg-white hover:bg-gradient-to-br hover:from-[#F3EBDD] hover:to-white border border-[#D8C4A8]/30 hover:border-[#E07A2F]/50 transition-all hover:shadow-2xl cursor-pointer"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#3E6B4E] to-[#1F3D2B] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <OptimizedImage
                      src={item?.Image}
                      alt={item.Title}
                      width={32}
                      height={32}
                      className="h-8 filter invert brightness-0"
                    />
                  </div>
                  <h3
                    className="text-xl mb-3 text-[#5A3A1E] group-hover:text-[#7A4A1D] transition-colors"
                    style={{ fontFamily: "Playfair Display, serif" }}
                  >
                    {item.Title}
                  </h3>
                  <p
                    className="text-[#5A3A1E]/85"
                    dangerouslySetInnerHTML={{
                      __html: cleanHeaderTags(decodeHtml(item.Description)),
                    }}
                  ></p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <div className="py-24 min-h-[600px]" />
      )}
      <div className="min-h-[500px]">
        {homeDetails &&
          homeDetails?.TourType &&
          homeDetails?.TourType?.length > 0 && (
            <PerfectForSection
              lookUpCMSData={lookUpCMSData}
              TourType={homeDetails?.TourType}
            />
          )}
      </div>
      <div className="min-h-[600px]">
        <DiningSection lookUpCMSData={lookUpCMSData} />
      </div>
      <div className="min-h-[800px]">
        {homeDetails &&
          homeDetails?.Activity &&
          homeDetails?.Activity?.length > 0 && (
            <ExperiencesSection
              lookUpCMSData={lookUpCMSData}
              Activity={homeDetails?.Activity}
            />
          )}
      </div>
      <div className="min-h-[600px]">
        {homeDetails &&
          homeDetails?.Testimonial &&
          homeDetails?.Testimonial?.length > 0 && (
            <TestimonialsSection
              lookUpCMSData={lookUpCMSData}
              Testimonial={homeDetails?.Testimonial}
            />
          )}
      </div>
      <div className="min-h-[480px]">
        {homeDetails &&
          homeDetails?.Gallery &&
          homeDetails?.Gallery?.length > 0 && (
            <GallerySection
              lookUpCMSData={lookUpCMSData}
              Images={homeDetails?.Gallery}
            />
          )}
      </div>
      <div className="min-h-[600px]">
        <GirSection lookUpCMSData={lookUpCMSData} />
      </div>
      <div className="min-h-[1000px]">
        {homeDetails && homeDetails?.FAQ && homeDetails?.FAQ?.length > 0 && (
          <FAQSection lookUpCMSData={lookUpCMSData} FAQ={homeDetails?.FAQ} />
        )}
      </div>
      <div className="min-h-[380px]">
        {lookUpCMSData?.["Home - SECTION 13: Final CTA"] && (
          <div
            dangerouslySetInnerHTML={{
              __html: cleanHeaderTags(
                decodeHtml(
                  lookUpCMSData?.["Home - SECTION 13: Final CTA"]?.Template,
                ),
              ),
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Home;
